/*
  PCG header (2026-10 redesign, after the Pop Report AppBar).

  <pcg-menu>   one desktop dropdown: a button plus the panel it aria-controls.
               Hover opens it (with a short grace period to cross the gap), click or Enter
               opens and pins it, a second click closes it. ArrowDown/ArrowUp on the button
               open it on the first/last item; inside, arrows, Home and End move, Esc closes
               and returns focus, Tab out or a click elsewhere closes. Only one is open at a time.
  <pcg-drawer> the phone/tablet drawer: slides in from the right under the bar, accordions
               instead of slide-in pages, Esc / backdrop / the burger close it, focus stays in it.

  Every lookup is guarded: the header can sit on a page with no normal content under it.
*/
(function () {
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  const openMenus = new Set();

  class PcgMenu extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.button = this.querySelector('[aria-controls]');
      this.panel = this.button && document.getElementById(this.button.getAttribute('aria-controls'));
      if (!this.button || !this.panel) return;
      this.ready = true;
      this.pinned = false;

      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.isOpen && this.pinned) {
          this.close();
        } else {
          this.open();
          this.pinned = true;
        }
      });
      this.button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.open();
          this.pinned = true;
          const items = this.items();
          const target = event.key === 'ArrowDown' ? items[0] : items[items.length - 1];
          if (target) target.focus();
        } else if (event.key === 'Escape' && this.isOpen) {
          event.preventDefault();
          this.close();
        }
      });
      this.panel.addEventListener('keydown', this.onPanelKeydown.bind(this));

      this.addEventListener('mouseenter', () => {
        if (!hoverCapable.matches) return;
        clearTimeout(this.closeTimer);
        this.open();
      });
      this.addEventListener('mouseleave', () => {
        if (!hoverCapable.matches || this.pinned) return;
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.close(), 140);
      });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        // Let a click land first; a focus move to nowhere (clicking empty space) is handled by the document listener.
        if (event.relatedTarget) this.close();
      });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    items() {
      return Array.from(this.panel.querySelectorAll('a[href], button:not([disabled])'));
    }

    open() {
      if (this.isOpen) return;
      openMenus.forEach((menu) => menu !== this && menu.close());
      this.setAttribute('open', '');
      this.button.setAttribute('aria-expanded', 'true');
      openMenus.add(this);
    }

    close(returnFocus) {
      clearTimeout(this.closeTimer);
      this.pinned = false;
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.button.setAttribute('aria-expanded', 'false');
      openMenus.delete(this);
      if (returnFocus) this.button.focus();
    }

    onPanelKeydown(event) {
      const items = this.items();
      const index = items.indexOf(document.activeElement);
      let next = null;
      switch (event.key) {
        case 'ArrowDown':
          next = items[(index + 1) % items.length];
          break;
        case 'ArrowUp':
          next = items[(index - 1 + items.length) % items.length];
          break;
        case 'Home':
          next = items[0];
          break;
        case 'End':
          next = items[items.length - 1];
          break;
        case 'Escape':
          event.preventDefault();
          this.close(true);
          return;
        default:
          return;
      }
      event.preventDefault();
      if (next) next.focus();
    }
  }

  document.addEventListener('click', (event) => {
    openMenus.forEach((menu) => {
      if (!menu.contains(event.target)) menu.close();
    });
  });

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class PcgDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.toggle = this.querySelector('.pcg-burger');
      this.drawer = this.querySelector('.pcg-drawer');
      this.panel = this.querySelector('.pcg-drawer__panel');
      if (!this.toggle || !this.drawer || !this.panel) return;
      this.ready = true;

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close(true) : this.open()));
      this.querySelectorAll('[data-pcg-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close(true)));
      // The cert search opens its own modal: get out of its way first.
      this.panel.querySelectorAll('[data-pcg-cert-open]').forEach((el) => el.addEventListener('click', () => this.close(false)));

      this.panel.querySelectorAll('.pcg-acc__btn[aria-controls]').forEach((button) => {
        button.addEventListener('click', () => {
          const target = document.getElementById(button.getAttribute('aria-controls'));
          if (!target) return;
          const expand = button.getAttribute('aria-expanded') !== 'true';
          button.setAttribute('aria-expanded', String(expand));
          target.hidden = !expand;
        });
      });

      this.onKeydown = this.onKeydown.bind(this);
      this.desktop = window.matchMedia('(min-width: 1200px)');
      this.desktop.addEventListener('change', () => this.desktop.matches && this.close(false));
    }

    get isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    open() {
      const bar = this.closest('.pcg-hdr__bar') || this.closest('header');
      const top = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0;
      this.drawer.style.setProperty('--pcg-drawer-top', `${top}px`);
      clearTimeout(this.hideTimer);
      this.drawer.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('pcg-drawer-open');
      requestAnimationFrame(() => requestAnimationFrame(() => this.drawer.classList.add('is-open')));
      document.addEventListener('keydown', this.onKeydown);
      const first = this.panel.querySelector(FOCUSABLE);
      if (first) setTimeout(() => first.focus({ preventScroll: true }), 50);
    }

    close(returnFocus) {
      if (!this.isOpen) return;
      this.drawer.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('pcg-drawer-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.hideTimer = setTimeout(() => {
        this.drawer.hidden = true;
      }, 260);
      if (returnFocus) this.toggle.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep focus inside the drawer and its burger (which closes it).
      const focusables = [this.toggle].concat(
        Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null)
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!focusables.includes(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('pcg-menu')) customElements.define('pcg-menu', PcgMenu);
  if (!customElements.get('pcg-drawer')) customElements.define('pcg-drawer', PcgDrawer);
})();
